import axios from 'axios'
import {getCoordinatesStore, getLanguageKey, getTokenKey} from 'helpers/localStorage'

function validateJWT(response) {
  return response
}

// ─── Header builder tách ra để tái sử dụng, tránh tính lại mỗi switch case ──
function buildHeaders() {
  const date = new Date()
  const offset = -date.getTimezoneOffset() / 60
  const gmtOffset = `GMT${offset >= 0 ? '+' : ''}${offset}`
  const localISOTime = new Date(
      date.getTime() - date.getTimezoneOffset() * 60000 + offset * 60000
  ).toISOString()
  const {latitude, longitude} = getCoordinatesStore()

  return {
    Authorization: 'Bearer ' + getTokenKey(),
    'Content-Type': 'application/json',
    'F-Lang': getLanguageKey(),
    'Accept-Language': getLanguageKey(),
    'F-Time': localISOTime,
    'F-Timezone': gmtOffset,
    'F-Latitude': latitude,
    'F-Longitude': longitude,
  }
}

// ─── AbortController registry ─────────────────────────────────────────────────
// Mỗi request được track theo key (thường là URL).
// Khi cùng key gọi lại (ví dụ navigate đi rồi về), request cũ bị cancel ngay.
// Dùng Map để O(1) lookup/delete.
const pendingRequests = new Map()

function registerRequest(key, controller) {
  // Cancel request cũ cùng key nếu còn đang chạy
  if (pendingRequests.has(key)) {
    pendingRequests.get(key).abort()
  }
  pendingRequests.set(key, controller)
}

function unregisterRequest(key) {
  pendingRequests.delete(key)
}

// ─── cancelRequest: dùng khi component unmount ────────────────────────────────
// Ví dụ trong useEffect: return () => cancelRequest(apiUrl)
export function cancelRequest(key) {
  if (pendingRequests.has(key)) {
    pendingRequests.get(key).abort()
    pendingRequests.delete(key)
  }
}

// ─── cancelAllRequests: dùng khi user logout hoặc navigate hard ───────────────
export function cancelAllRequests() {
  pendingRequests.forEach(controller => controller.abort())
  pendingRequests.clear()
}

export const callAPI = (method, api, data, options = {}) => {
  const headers = buildHeaders()

  // ─── AbortController cho request này ──────────────────────────────────────
  // options.signal: cho phép caller tự truyền signal vào (useEffect pattern)
  // Nếu không truyền signal, tự tạo controller và track theo URL
  const controller = new AbortController()
  const requestKey = options.requestKey || `${method}:${api}`

  registerRequest(requestKey, controller)

  // Merge signal từ ngoài vào nếu có (ví dụ từ AbortController của useEffect)
  // Khi một trong hai abort thì request bị cancel
  const signal = options.signal
      ? anySignal([controller.signal, options.signal])
      : controller.signal

  const axiosConfig = {
    headers,
    signal,
  }

  const handleResponse = (response) => {
    unregisterRequest(requestKey)
    return validateJWT(response)
  }

  const handleError = (error) => {
    unregisterRequest(requestKey)
    // Phân biệt cancel (bình thường) vs lỗi thật
    if (axios.isCancel(error) || error.name === 'AbortError' || error.name === 'CanceledError') {
      // Trả về null thay vì throw để caller không cần try/catch
      return null
    }
    return error.response
  }

  switch (method) {
    case 'GET':
      return axios
          .get(api, axiosConfig)
          .then(handleResponse)
          .catch(handleError)

    case 'POST':
      return axios
          .post(api, data, axiosConfig)
          .then(handleResponse)
          .catch(handleError)

    case 'DELETE':
      return axios
          .delete(api, {...axiosConfig, data})
          .then(handleResponse)
          .catch(handleError)

    case 'PUT':
      return axios
          .put(api, data, axiosConfig)
          .then(handleResponse)
          .catch(handleError)

    default:
      return null
  }
}

// ─── anySignal: kết hợp nhiều AbortSignal thành 1 ────────────────────────────
// Polyfill cho AbortSignal.any() chưa được support rộng
function anySignal(signals) {
  if (typeof AbortSignal.any === 'function') {
    return AbortSignal.any(signals)
  }
  const controller = new AbortController()
  signals.forEach(signal => {
    if (signal.aborted) {
      controller.abort()
    } else {
      signal.addEventListener('abort', () => controller.abort(), {once: true})
    }
  })
  return controller.signal
}

const Request = {
  get(api, data, options) {
    return callAPI('GET', api, data, options)
  },
  post(api, data, options) {
    return callAPI('POST', api, data, options)
  },
  delete(api, data, options) {
    return callAPI('DELETE', api, data, options)
  },
  put(api, data, options) {
    return callAPI('PUT', api, data, options)
  },
}

window &&
(() => {
  window.appRequest = Request
})()

export default Request

// ─── HƯỚNG DẪN DÙNG ──────────────────────────────────────────────────────────
//
// 1. TỰ ĐỘNG (mặc định, không cần sửa gì thêm):
//    Mỗi request GET cùng URL sẽ tự cancel request cũ.
//    Ví dụ: user navigate nhanh qua nhiều nhà thờ → chỉ request cuối cùng chạy.
//
// 2. CANCEL KHI UNMOUNT (dùng trong useEffect):
//    import { cancelRequest } from 'helpers/request'
//
//    useEffect(() => {
//      fetchChurchData()
//      return () => cancelRequest(`GET:/api/church/${id}`)
//    }, [id])
//
// 3. CANCEL TỪ NGOÀI (truyền signal vào):
//    const controller = new AbortController()
//    Request.get('/api/church/123', null, { signal: controller.signal })
//    // sau đó:
//    controller.abort()
//
// 4. CANCEL TẤT CẢ (logout hoặc hard navigate):
//    import { cancelAllRequests } from 'helpers/request'
//    cancelAllRequests()